import { Component, computed, effect, inject, input, OnInit, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TimeSpan, WeeklySchedule } from '../../../../shared/models/generated/ziroModels';
import { NgClass } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles, ZiroButtonComponent } from '../../../../button/button.component';
import { WeeklyScheduleService } from '../../../weekly-schedule.service';
import { TimeRange } from '../../../../forms/fields/time-range/ziro-time-range-form.component';
import {
  DayScheduleType,
  WeeklyScheduleDayModalComponent,
  WeeklyScheduleDayModalData,
  WeeklyScheduleDayModalResult,
} from './weekly-schedule-day-modal/weekly-schedule-day-modal.component';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { Observable, of, Subscription } from 'rxjs';
import { PipesModule } from '../../../../shared/pipes/pipes.module';
import { UiCallFlow } from '../call-flows-side-nav/call-flows-side-nav.component';
import {
  ZiroTimeRangesComponent,
  ZiroTimeRangesConfig,
} from '../../../../forms/fields/time-range/ziro-time-ranges.component';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { isEqual } from 'lodash';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';

export type DayKey = keyof Omit<WeeklySchedule, 'name'>;

export interface WeeklyScheduleDayEntity {
  slots: TimeRange[];
}

export interface DayConfig {
  key: DayKey;
  label: string;
  shortLabel: string;
}

export const DAYS: DayConfig[] = [
  { key: 'mondayHours', label: 'Monday', shortLabel: 'MON' },
  { key: 'tuesdayHours', label: 'Tuesday', shortLabel: 'TUE' },
  { key: 'wednesdayHours', label: 'Wednesday', shortLabel: 'WED' },
  { key: 'thursdayHours', label: 'Thursday', shortLabel: 'THU' },
  { key: 'fridayHours', label: 'Friday', shortLabel: 'FRI' },
  { key: 'saturdayHours', label: 'Saturday', shortLabel: 'SAT' },
  { key: 'sundayHours', label: 'Sunday', shortLabel: 'SUN' },
];

const WEEKDAY_KEYS: DayKey[] = ['mondayHours', 'tuesdayHours', 'wednesdayHours', 'thursdayHours', 'fridayHours'];
const WEEKEND_KEYS: DayKey[] = ['saturdayHours', 'sundayHours'];

@Component({
  selector: 'ziro-weekly-schedule',
  standalone: true,
  imports: [
    FormsModule,
    NgClass,
    TranslateModule,
    NgbTooltip,
    PipesModule,
    ZiroTimeRangesComponent,
    ZiroButtonComponent,
    ZiroFormsModule,
  ],
  templateUrl: './weekly-schedule.component.html',
  styleUrl: './weekly-schedule.component.scss',
  providers: [WeeklyScheduleService],
})
export class WeeklyScheduleComponent extends ZiroFormAbstractDirective<WeeklyScheduleDayEntity> implements OnInit {
  private _translateService = inject(TranslateService);
  private _weeklyScheduleService = inject(WeeklyScheduleService);
  private _modalService = inject(ZiroModalService);

  schedule = input<WeeklySchedule>();
  callFlow = input.required<UiCallFlow>();

  scheduleChange = output<WeeklySchedule>();
  selectBusinessHoursFlow = output<void>();

  isCustom = signal(false);
  localSchedule = signal<WeeklySchedule | null>(null);

  readonly days = DAYS;

  isQuickRangeInvalid = false;

  formConfig = {
    fields: {
      slots: {
        label: 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.weekly_schedule.quick_set',
        dataAutomation: 'weekly-schedule-day-slot-quick-set',
        componentConfig: new ZiroTimeRangesConfig(),
      },
    },
  };

  private readonly _allDayKeys = [...WEEKDAY_KEYS, ...WEEKEND_KEYS];
  private readonly _allDaySpan: TimeSpan = {
    start: '00:00',
    end: '1.00:00:00',
  };
  private _savedCustomSchedule = signal<WeeklySchedule | null>(null);
  private _scheduleIsCustom = computed(() => {
    const weeklySchedule = this.schedule();
    if (!weeklySchedule) {
      return false;
    }

    return this.days.some((day) => {
      const spans = weeklySchedule[day.key] as TimeSpan[];
      if (!spans?.length) {
        return true;
      }
      return spans.some((span) => !this._isOpenAllDaySpan(span));
    });
  });
  private _subs = new Subscription();

  constructor() {
    super();

    effect(() => {
      const schedule = this.schedule();
      if (!schedule) {
        return;
      }

      const currentLocal = untracked(() => this.localSchedule());
      if (!isEqual(schedule, currentLocal)) {
        this.isCustom.set(this._scheduleIsCustom());
        this.localSchedule.set({ ...schedule });
      }
    });
  }

  ngOnInit(): void {
    const formUpdateSub = this.ziroFormsUpdate$.subscribe((update) => {
      if (!isEqual(update.new, update.old)) {
        this.isQuickRangeInvalid = update.new.slots[0].end < update.new.slots[0].start;
      }
    });
    this._subs.add(formUpdateSub);

    this.entitySource.next({
      slots: [
        {
          start: '09:00',
          end: '17:00',
        },
      ],
    });
  }

  isDayOpen(key: DayKey): boolean {
    return this.getDayHours(key).length > 0;
  }

  getDayHours(key: DayKey): TimeSpan[] {
    return (this._effectiveSchedule()[key] as TimeSpan[]) ?? [];
  }

  getVisibleScheduleLabel(spans: TimeSpan[]): string {
    if (!spans?.length) {
      return '';
    }

    return this.formatScheduleSpan(spans[0].start, spans[0].end);
  }

  getRemainingScheduleCount(spans: TimeSpan[]): number {
    return spans?.length > 1 ? spans.length - 1 : 0;
  }

  formatScheduleSpan(start: string, end: string): string {
    if (!start || !end) {
      return '';
    }

    const startSeconds = this._weeklyScheduleService.parseTimeToSeconds(start);
    const endSeconds = this._weeklyScheduleService.parseDurationToSeconds(end);

    if (this._isOpenAllDayInSeconds(startSeconds, endSeconds)) {
      return this._translateService.instant(
        'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.weekly_schedule.open_all_day',
      );
    }

    const formattedStart = this._weeklyScheduleService.formatSecondsTo12Hour(startSeconds);
    const formattedEnd = this._weeklyScheduleService.formatSecondsTo12Hour(endSeconds % (24 * 60 * 60));

    return `${formattedStart} – ${formattedEnd}`;
  }

  selectDay(key: DayKey): void {
    const day = this.days.find((d) => d.key === key);
    const existingSpans = this.getDayHours(key);
    const scheduleType = this._getDayScheduleType(existingSpans);

    const slots: TimeRange[] = existingSpans.map((span) => ({
      start: this._weeklyScheduleService.toTimeInputValue(span.start, '09:00'),
      end: this._weeklyScheduleService.durationToTimeInputValue(span.end, '17:00'),
    }));

    const modalData: WeeklyScheduleDayModalData = {
      dayLabel: day?.label ?? key,
      scheduleType,
      slots: slots.length ? slots : [{ start: '09:00', end: '17:00' }],
      submitFn: (result: WeeklyScheduleDayModalResult) => of(result),
    };

    const modalViewProperties = {
      title: this._translateService.instant(
        'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.weekly_schedule.time_ranges.modal.title',
      ),
      bodyClass: WeeklyScheduleDayModalComponent,
      data: modalData,
      size: 'md',
    };

    this._modalService
      .openDetailedModal(null, {
        bodyClass: WeeklyScheduleDayModalComponent,
        modalViewProperties,
        size: 'md',
      })
      .subscribe((result: WeeklyScheduleDayModalResult | null) => {
        if (result === null || result === undefined) {
          return;
        }

        const newTimeSpans = this._toTimeSpans(result);
        const updated = { ...this._effectiveSchedule(), [key]: newTimeSpans };

        this._setScheduleAndEmit(updated);
      });
  }

  toggleCustom(): void {
    const shouldShowCustomGrid = !this.isCustom();

    this.isCustom.set(shouldShowCustomGrid);

    if (shouldShowCustomGrid) {
      this._showCustomGrid();
      return;
    }

    this._switchToOpenAllDay();
  }

  applyWeekdays(): void {
    this._applySpanToKeys(WEEKDAY_KEYS);
  }

  applyWeekendDays(): void {
    this._applySpanToKeys(WEEKEND_KEYS);
  }

  submit(): Observable<null> {
    return of(null);
  }

  private _showCustomGrid(): void {
    const flowName = this._getFlowName();
    const savedSchedule = this._savedCustomSchedule();
    if (savedSchedule) {
      this._setScheduleAndEmit({
        ...savedSchedule,
        name: flowName,
      });
      return;
    }

    const populatedSchedule = this._buildScheduleForAllDays(this._allDaySpan, {
      ...this._effectiveSchedule(),
      name: flowName,
    } as WeeklySchedule);
    this.localSchedule.set(populatedSchedule);
  }

  private _switchToOpenAllDay(): void {
    const flowName = this._getFlowName();
    this._savedCustomSchedule.set({
      ...this._effectiveSchedule(),
      name: flowName,
    });

    const openAllDaySchedule = this._buildScheduleForAllDays(this._allDaySpan, {
      ...this._effectiveSchedule(),
      name: flowName,
    });
    this._setScheduleAndEmit(openAllDaySchedule);
  }

  private _getDayScheduleType(spans: TimeSpan[]): DayScheduleType {
    if (!spans.length) {
      return DayScheduleType.CLOSED;
    }

    if (spans.length === 1 && this._isOpenAllDaySpan(spans[0])) {
      return DayScheduleType.OPEN_24H;
    }

    return DayScheduleType.CUSTOM_HOURS;
  }

  private _toTimeSpans(result: WeeklyScheduleDayModalResult): TimeSpan[] {
    if (result.scheduleType === DayScheduleType.OPEN_24H) {
      return [{ ...this._allDaySpan }];
    }

    if (result.scheduleType === DayScheduleType.CLOSED) {
      return [];
    }

    return result.slots.map((slot) => ({
      start: slot.start,
      end: this._timeToApiDuration(slot.end),
    }));
  }

  private _buildScheduleForAllDays(span: TimeSpan, baseSchedule: WeeklySchedule): WeeklySchedule {
    return this._allDayKeys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: [{ ...span }],
      }),
      baseSchedule,
    );
  }

  private _setScheduleAndEmit(schedule: WeeklySchedule): void {
    this.localSchedule.set(schedule);
    this.scheduleChange.emit(schedule);
  }

  private _effectiveSchedule(): WeeklySchedule {
    return this.localSchedule() ?? this.schedule() ?? ({} as WeeklySchedule);
  }

  private _applySpanToKeys(keys: DayKey[]): void {
    const span: TimeSpan = {
      start: this._timeToApiDuration(this.entity.slots[0].start),
      end: this._timeToApiDuration(this.entity.slots[0].end),
    };

    const updated = keys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: [{ ...span }],
      }),
      { ...this._effectiveSchedule() },
    );
    this._setScheduleAndEmit(updated);
  }

  private _isOpenAllDaySpan(span: TimeSpan): boolean {
    const startSeconds = this._weeklyScheduleService.parseTimeToSeconds(span.start);
    const endSeconds = this._weeklyScheduleService.parseDurationToSeconds(span.end);

    return this._isOpenAllDayInSeconds(startSeconds, endSeconds);
  }

  private _isOpenAllDayInSeconds(startSeconds: number, endSeconds: number): boolean {
    return startSeconds === 0 && endSeconds === 24 * 60 * 60;
  }

  private _getFlowName(): string {
    return this.callFlow()?.name || 'After Hours';
  }

  private _timeToApiDuration(time: string): string {
    return `${time}:00`;
  }

  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ButtonStyles = ButtonStyles;
  protected readonly ButtonSizes = ButtonSizes;
}
